/**
 * White label (Balkan): prevodi za "sr" su latinicom, ali Intl za "sr" formatira datume cirilicom
 * ("септембар", "ПОН"). Ovaj modul globalno preusmerava "sr" na "sr-Latn" u formatiranju datuma,
 * da bi kalendar i tekst bili istim pismom. Uvozi se jednom, na serveru i na klijentu.
 */
const LOCALE_MAP: Record<string, string> = { sr: "sr-Latn", "sr-RS": "sr-Latn-RS" };

type Locales = string | string[] | undefined;

function mapLocales(locales: Locales): Locales {
  if (typeof locales === "string") return LOCALE_MAP[locales] ?? locales;
  if (Array.isArray(locales)) return locales.map((l) => LOCALE_MAP[l] ?? l);
  return locales;
}

const PATCHED = Symbol.for("calcom.intlLatinLocales");

/**
 * Mora se eksplicitno pozvati: @calcom/lib ima "sideEffects": false, pa bi bundler
 * izbacio import koji samo menja globalno stanje.
 */
export function applyIntlLatinLocales(): void {
  const g = globalThis as unknown as Record<symbol, boolean | undefined>;
  if (g[PATCHED]) return;
  g[PATCHED] = true;

  const OriginalDateTimeFormat = Intl.DateTimeFormat;
  const PatchedDateTimeFormat = function (locales?: Locales, options?: Intl.DateTimeFormatOptions) {
    return new OriginalDateTimeFormat(mapLocales(locales), options);
  };
  Object.defineProperty(PatchedDateTimeFormat, "prototype", { value: OriginalDateTimeFormat.prototype });
  Object.defineProperty(PatchedDateTimeFormat, "supportedLocalesOf", {
    value: OriginalDateTimeFormat.supportedLocalesOf,
  });
  Object.defineProperty(Intl, "DateTimeFormat", {
    value: PatchedDateTimeFormat,
    writable: true,
    configurable: true,
  });

  for (const method of ["toLocaleDateString", "toLocaleTimeString", "toLocaleString"] as const) {
    const original = Date.prototype[method] as (
      this: Date,
      locales?: Locales,
      options?: Intl.DateTimeFormatOptions
    ) => string;
    Object.defineProperty(Date.prototype, method, {
      value: function (this: Date, locales?: Locales, options?: Intl.DateTimeFormatOptions) {
        return original.call(this, mapLocales(locales), options);
      },
      writable: true,
      configurable: true,
    });
  }
}
